import {
  INPUT_ACTION,
  INPUT_CLOSE_PANEL,
  INPUT_DOWN,
  INPUT_LEFT,
  INPUT_OPEN_DEBUG_PANEL,
  INPUT_OPEN_BUILD_MENU,
  INPUT_OPEN_INVENTORY,
  INPUT_OPEN_TECH_TREE,
  INPUT_RIGHT,
  INPUT_UP,
  type BuildCommand,
  type Input,
  type TechId,
  type UiFocusScope,
  type ViewModel,
} from '../contract.js';
import { TILE_SIZE } from '../domain/content.js';
import {
  computeActionButtonRect,
  computeBuildButtonRects,
  computeBuildMenuRects,
  computeBuildingPanelRects,
  computeDebugPanelRects,
  computeInventoryButtonRect,
  computeInventoryPanelRects,
  computeScreenLayout,
  computeTechTreeRects,
  computeToolsPanelRects,
  contains,
  type ScreenLayout,
} from './layout.js';
import {
  PREVENT_KEYS,
  buildCommandForKey,
  debugCommandForKey,
  heldMaskForKey,
  isActionKey,
  itemCommandForKey,
  menuNavigateForKey,
  panelCommandForKey,
  panelCommandIndex,
  queuedMaskForKey,
  techCommandForKey,
  toolCommandForKey,
} from './input-bindings.js';

export class InputBridge {
  private heldMask = 0;
  private queuedMask = 0;
  private latestView: ViewModel | null = null;
  private queuedTargetTileX: number | undefined;
  private queuedTargetTileY: number | undefined;
  private queuedSelectedSlot: number | undefined;
  private queuedInventoryMove: Input['inventoryMove'] | undefined;
  private queuedTransferItem: Input['transferItem'] | undefined;
  private queuedPanelItemMove: Input['panelItemMove'] | undefined;
  private queuedUiFocus: Input['uiFocus'] | undefined;
  private queuedPanelCommand: Input['panelCommand'] | undefined;
  private queuedBuildCommand: Input['buildCommand'] | undefined;
  private queuedTechCommand: Input['techCommand'] | undefined;
  private queuedItemCommand: Input['itemCommand'] | undefined;
  private queuedToolCommand: Input['toolCommand'] | undefined;
  private queuedDebugCommand: Input['debugCommand'] | undefined;
  private heldAction = false;
  private activeActionPointer: number | null = null;
  private activeActionPointerMode: 'button' | 'world' | null = null;
  private heldTargetTileX: number | undefined;
  private heldTargetTileY: number | undefined;
  private activeInventoryDragSlot: number | null = null;
  private activeBuildingPanelDrag: { location: NonNullable<Input['panelItemMove']>['from']; slot: number } | null = null;
  private readonly tooltip: HTMLDivElement;
  private readonly downKeys = new Set<string>();
  private readonly previousGamepadButtons = new Set<string>();

  constructor(private readonly container: HTMLElement) {
    this.tooltip = document.createElement('div');
    this.tooltip.className = 'temptrash-tooltip';
    this.tooltip.hidden = true;
    container.appendChild(this.tooltip);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    container.addEventListener('pointerdown', this.onPointerDown);
    container.addEventListener('pointermove', this.onPointerMove);
    container.addEventListener('pointerup', this.onPointerUp);
    container.addEventListener('pointercancel', this.onPointerUp);
  }

  setView(view: ViewModel): void {
    this.latestView = view;
  }

  sample(): Input {
    const gamepad = this.sampleGamepad();
    const input: Input = {
      mask: this.heldMask | this.queuedMask | gamepad.heldMask | gamepad.queuedMask,
      actionHeld: this.heldAction || gamepad.actionHeld,
    };
    if (this.queuedSelectedSlot !== undefined) input.selectedSlot = this.queuedSelectedSlot;
    if (this.queuedInventoryMove !== undefined) input.inventoryMove = this.queuedInventoryMove;
    if (this.queuedTransferItem !== undefined) input.transferItem = this.queuedTransferItem;
    if (this.queuedPanelItemMove !== undefined) input.panelItemMove = this.queuedPanelItemMove;
    if (this.queuedUiFocus !== undefined) input.uiFocus = this.queuedUiFocus;
    if (this.queuedPanelCommand !== undefined) input.panelCommand = this.queuedPanelCommand;
    if (this.queuedBuildCommand !== undefined) input.buildCommand = this.queuedBuildCommand;
    if (this.queuedTechCommand !== undefined) input.techCommand = this.queuedTechCommand;
    if (this.queuedItemCommand !== undefined) input.itemCommand = this.queuedItemCommand;
    if (this.queuedToolCommand !== undefined) input.toolCommand = this.queuedToolCommand;
    if (this.queuedDebugCommand !== undefined) input.debugCommand = this.queuedDebugCommand;
    if (this.queuedTargetTileX !== undefined && this.queuedTargetTileY !== undefined) {
      input.targetTileX = this.queuedTargetTileX;
      input.targetTileY = this.queuedTargetTileY;
    } else if (this.heldAction && this.heldTargetTileX !== undefined && this.heldTargetTileY !== undefined) {
      input.targetTileX = this.heldTargetTileX;
      input.targetTileY = this.heldTargetTileY;
    }
    this.clearQueuedOneShots();
    return input;
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.container.removeEventListener('pointerdown', this.onPointerDown);
    this.container.removeEventListener('pointermove', this.onPointerMove);
    this.container.removeEventListener('pointerup', this.onPointerUp);
    this.container.removeEventListener('pointercancel', this.onPointerUp);
    this.tooltip.remove();
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    this.tooltip.hidden = true;
    if (PREVENT_KEYS.has(event.key) || PREVENT_KEYS.has(event.code)) event.preventDefault();
    const wasDown = this.downKeys.has(event.code);
    this.downKeys.add(event.code);

    const modalScope = this.activeFocusScope();
    if (modalScope !== null) {
      this.handleModalKey(event, wasDown, modalScope);
      return;
    }

    this.heldMask |= heldMaskForKey(event);
    if (isActionKey(event)) this.heldAction = true;
    if (!event.repeat && !wasDown) {
      const itemCommand = itemCommandForKey(event);
      if (itemCommand !== null) this.queuedItemCommand = itemCommand;
      else this.queuedMask |= queuedMaskForKey(event);
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (PREVENT_KEYS.has(event.key) || PREVENT_KEYS.has(event.code)) event.preventDefault();
    this.downKeys.delete(event.code);
    this.heldMask &= ~heldMaskForKey(event);
    if (isActionKey(event)) this.heldAction = false;
  };

  private readonly onBlur = (): void => {
    this.heldMask = 0;
    this.heldAction = false;
    this.downKeys.clear();
    this.activeActionPointer = null;
    this.activeActionPointerMode = null;
    this.heldTargetTileX = undefined;
    this.heldTargetTileY = undefined;
    this.activeInventoryDragSlot = null;
    this.activeBuildingPanelDrag = null;
    this.clearQueuedOneShots();
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    const local = this.localPoint(event);
    const layout = computeScreenLayout(this.container.clientWidth, this.container.clientHeight);
    this.updateTooltip(local.x, local.y, layout);

    if (this.handleInventoryPointer(local.x, local.y, layout)) {
      event.preventDefault();
      return;
    }
    if (this.handleBuildMenuPointer(local.x, local.y, layout)) {
      event.preventDefault();
      return;
    }
    if (this.handleTechTreePointer(local.x, local.y, layout)) {
      event.preventDefault();
      return;
    }
    if (this.handleToolsPanelPointer(local.x, local.y, layout)) {
      event.preventDefault();
      return;
    }
    if (this.handleDebugPanelPointer(local.x, local.y, layout)) {
      event.preventDefault();
      return;
    }
    if (this.handleBuildingPanelPointer(local.x, local.y, layout, event.shiftKey)) {
      event.preventDefault();
      return;
    }

    const inventoryRect = computeInventoryButtonRect(layout);
    if (contains(inventoryRect, local.x, local.y)) {
      this.queuedMask |= INPUT_OPEN_INVENTORY;
      event.preventDefault();
      return;
    }

    const actionRect = computeActionButtonRect(layout);
    if (contains(actionRect, local.x, local.y)) {
      this.queuedMask |= INPUT_ACTION;
      this.heldAction = true;
      this.activeActionPointer = event.pointerId;
      this.activeActionPointerMode = 'button';
      this.container.setPointerCapture(event.pointerId);
      event.preventDefault();
      return;
    }

    const buildRects = computeBuildButtonRects(layout);
    if (contains(buildRects.build, local.x, local.y)) {
      this.queuedMask |= INPUT_OPEN_BUILD_MENU;
      event.preventDefault();
      return;
    }
    if (contains(buildRects.tech, local.x, local.y)) {
      this.queuedMask |= INPUT_OPEN_TECH_TREE;
      event.preventDefault();
      return;
    }

    if (this.queueWorldTileAction(local.x, local.y, true)) {
      this.activeActionPointer = event.pointerId;
      this.activeActionPointerMode = 'world';
      this.container.setPointerCapture(event.pointerId);
      event.preventDefault();
    }
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    const local = this.localPoint(event);
    const layout = computeScreenLayout(this.container.clientWidth, this.container.clientHeight);
    this.updateTooltip(local.x, local.y, layout);
    if (event.pointerId === this.activeActionPointer && this.activeActionPointerMode === 'world') {
      this.queueWorldTileAction(local.x, local.y, false);
      event.preventDefault();
    }
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    const local = this.localPoint(event);
    const layout = computeScreenLayout(this.container.clientWidth, this.container.clientHeight);
    if (this.activeInventoryDragSlot !== null) {
      this.finishInventoryDrag(local.x, local.y, layout);
      this.activeInventoryDragSlot = null;
      event.preventDefault();
      return;
    }
    if (this.activeBuildingPanelDrag !== null) {
      this.finishBuildingPanelDrag(local.x, local.y, layout);
      this.activeBuildingPanelDrag = null;
      event.preventDefault();
      return;
    }
    if (event.pointerId === this.activeActionPointer) {
      this.heldAction = false;
      this.activeActionPointer = null;
      this.activeActionPointerMode = null;
      this.heldTargetTileX = undefined;
      this.heldTargetTileY = undefined;
      event.preventDefault();
    }
  };

  private handleModalKey(event: KeyboardEvent, wasDown: boolean, scope: UiFocusScope): void {
    if (
      event.code === 'Escape' ||
      event.code === 'KeyB' ||
      (scope === 'inventory' && event.code === 'KeyI') ||
      (scope === 'techTree' && event.code === 'KeyT') ||
      (scope === 'toolsPanel' && event.code === 'KeyC') ||
      (scope === 'debugPanel' && event.code === 'KeyB')
    ) {
      if (!event.repeat && !wasDown) this.queuedMask |= INPUT_CLOSE_PANEL;
      return;
    }
    if (event.repeat || wasDown) return;

    if (scope === 'buildMenu') {
      const command = buildCommandForKey(event);
      if (command !== null) this.queuedBuildCommand = command;
      else if (isActionKey(event)) this.queuedBuildCommand = this.buildCommandForCurrentFocus();
      else this.queueFocusNavigation(event);
      return;
    }

    if (scope === 'techTree') {
      const command = techCommandForKey(event);
      if (command !== null) this.queuedTechCommand = command;
      else if (isActionKey(event)) this.queuedTechCommand = this.techCommandForCurrentFocus();
      else this.queueFocusNavigation(event);
      return;
    }

    if (scope === 'toolsPanel') {
      const command = toolCommandForKey(event);
      if (command !== null) this.queuedToolCommand = command;
      else if (isActionKey(event)) this.queuedToolCommand = this.toolCommandForCurrentFocus();
      else this.queueFocusNavigation(event);
      return;
    }

    if (scope === 'debugPanel') {
      const command = debugCommandForKey(event);
      if (command !== null) this.queuedDebugCommand = command;
      else if (isActionKey(event)) this.queuedDebugCommand = this.debugCommandForCurrentFocus();
      else this.queueFocusNavigation(event);
      return;
    }

    if (scope === 'buildingPanel') {
      const command = panelCommandForKey(event);
      if (command !== null) this.queuedPanelCommand = command;
      else if (isActionKey(event)) this.queuedPanelCommand = this.panelCommandForCurrentFocus();
      else this.queueFocusNavigation(event);
      return;
    }

    this.queueFocusNavigation(event);
  }

  private handleInventoryPointer(x: number, y: number, layout: ScreenLayout): boolean {
    const view = this.latestView;
    if (view?.inventoryPanel.open !== true) return false;
    const panel = computeInventoryPanelRects(layout, view.inventoryPanel.slots.length);
    const slot = panel.slots.findIndex((rect) => contains(rect, x, y));
    if (slot >= 0) {
      this.queuedSelectedSlot = slot;
      this.queueAbsoluteFocus('inventory', slot);
      this.activeInventoryDragSlot = slot;
    } else if (contains(panel.close, x, y) || !contains(panel.panel, x, y)) {
      this.queuedMask |= INPUT_CLOSE_PANEL;
    }
    return true;
  }

  private finishInventoryDrag(x: number, y: number, layout: ScreenLayout): void {
    const view = this.latestView;
    if (view?.inventoryPanel.open !== true || this.activeInventoryDragSlot === null) return;
    const panel = computeInventoryPanelRects(layout, view.inventoryPanel.slots.length);
    const slot = panel.slots.findIndex((rect) => contains(rect, x, y));
    if (slot >= 0) this.queuedInventoryMove = {
      fromSlot: this.activeInventoryDragSlot,
      toSlot: slot,
    };
  }

  private handleBuildMenuPointer(x: number, y: number, layout: ScreenLayout): boolean {
    if (this.latestView?.buildMenu.open !== true) return false;
    const menu = computeBuildMenuRects(layout);
    const optionIndex = menu.options.findIndex((rect) => contains(rect, x, y));
    if (optionIndex >= 0) {
      this.queueAbsoluteFocus('buildMenu', optionIndex);
      this.queuedBuildCommand = this.latestView?.buildMenu.options[optionIndex]?.type;
    } else if (contains(menu.close, x, y) || !contains(menu.panel, x, y)) {
      this.queuedBuildCommand = 'cancel';
    }
    return true;
  }

  private handleTechTreePointer(x: number, y: number, layout: ScreenLayout): boolean {
    if (this.latestView?.techTree.open !== true) return false;
    const menu = computeTechTreeRects(layout);
    const optionIndex = menu.options.findIndex((rect) => contains(rect, x, y));
    if (optionIndex >= 0) {
      this.queueAbsoluteFocus('techTree', optionIndex);
      this.queuedTechCommand = this.latestView?.techTree.options[optionIndex]?.id;
    } else if (contains(menu.close, x, y) || !contains(menu.panel, x, y)) {
      this.queuedMask |= INPUT_CLOSE_PANEL;
    }
    return true;
  }

  private handleToolsPanelPointer(x: number, y: number, layout: ScreenLayout): boolean {
    if (this.latestView?.toolsPanel.open !== true) return false;
    const menu = computeToolsPanelRects(layout);
    const optionIndex = menu.options.findIndex((rect) => contains(rect, x, y));
    if (optionIndex >= 0) {
      this.queueAbsoluteFocus('toolsPanel', optionIndex);
      this.queuedToolCommand = this.latestView?.toolsPanel.recipes[optionIndex]?.id;
    } else if (contains(menu.close, x, y) || !contains(menu.panel, x, y)) {
      this.queuedMask |= INPUT_CLOSE_PANEL;
    }
    return true;
  }

  private handleDebugPanelPointer(x: number, y: number, layout: ScreenLayout): boolean {
    if (this.latestView?.debugPanel.open !== true) return false;
    const menu = computeDebugPanelRects(layout);
    const optionIndex = menu.options.findIndex((rect) => contains(rect, x, y));
    if (optionIndex >= 0) {
      this.queueAbsoluteFocus('debugPanel', optionIndex);
      this.queuedDebugCommand = this.latestView?.debugPanel.options[optionIndex]?.command;
    } else if (contains(menu.close, x, y) || !contains(menu.panel, x, y)) {
      this.queuedMask |= INPUT_CLOSE_PANEL;
    }
    return true;
  }

  private handleBuildingPanelPointer(x: number, y: number, layout: ScreenLayout, shiftKey: boolean): boolean {
    const panelView = this.latestView?.buildingPanel;
    if (panelView === null || panelView === undefined) return false;
    const panel = computeBuildingPanelRects(layout, panelView.playerInventory.length, panelView.inbox.length, panelView.outbox.length, panelView.storage.length);

    if (shiftKey) {
      const playerSlot = panel.playerSlots.findIndex((rect) => contains(rect, x, y));
      if (playerSlot >= 0) {
        this.queuedTransferItem = { from: 'player', slot: playerSlot };
        return true;
      }
      const inputSlot = panel.inboxSlots.findIndex((rect) => contains(rect, x, y));
      if (inputSlot >= 0) {
        this.queuedTransferItem = { from: 'buildingInput', slot: inputSlot };
        return true;
      }
      const outputSlot = panel.outboxSlots.findIndex((rect) => contains(rect, x, y));
      if (outputSlot >= 0) {
        this.queuedTransferItem = { from: 'buildingOutput', slot: outputSlot };
        return true;
      }
      const storageSlot = panel.storageSlots.findIndex((rect) => contains(rect, x, y));
      if (storageSlot >= 0) {
        this.queuedTransferItem = { from: 'buildingStorage', slot: storageSlot };
        return true;
      }
    }

    const dragSource = this.buildingPanelSlotAt(x, y, panel);
    if (dragSource !== null) {
      this.activeBuildingPanelDrag = dragSource;
      if (dragSource.location === 'player') {
        this.queuedSelectedSlot = dragSource.slot;
        this.queueAbsoluteFocus('inventory', dragSource.slot);
      }
      return true;
    }

    if (contains(panel.deposit, x, y)) {
      this.queueAbsoluteFocus('buildingPanel', 0);
      this.queuedPanelCommand = 'depositSelected';
    } else if (contains(panel.depositAll, x, y)) {
      this.queueAbsoluteFocus('buildingPanel', 1);
      this.queuedPanelCommand = 'depositAll';
    } else if (contains(panel.collect, x, y)) {
      this.queueAbsoluteFocus('buildingPanel', 2);
      this.queuedPanelCommand = 'collectOutput';
    } else if (contains(panel.remove, x, y)) {
      this.queueAbsoluteFocus('buildingPanel', 3);
      this.queuedPanelCommand = 'removeBuilding';
    } else if (contains(panel.close, x, y)) {
      this.queueAbsoluteFocus('buildingPanel', 4);
      this.queuedPanelCommand = 'close';
    } else if (!contains(panel.panel, x, y)) {
      this.queuedPanelCommand = 'close';
    }
    return true;
  }

  private finishBuildingPanelDrag(x: number, y: number, layout: ScreenLayout): void {
    const panelView = this.latestView?.buildingPanel;
    if (panelView === null || panelView === undefined || this.activeBuildingPanelDrag === null) return;
    const panel = computeBuildingPanelRects(layout, panelView.playerInventory.length, panelView.inbox.length, panelView.outbox.length, panelView.storage.length);
    const target = this.buildingPanelSlotAt(x, y, panel);
    if (target === null) return;
    this.queuedPanelItemMove = {
      from: this.activeBuildingPanelDrag.location,
      fromSlot: this.activeBuildingPanelDrag.slot,
      to: target.location,
      toSlot: target.slot,
    };
  }

  private localPoint(event: PointerEvent): { x: number; y: number } {
    const bounds = this.container.getBoundingClientRect();
    return {
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    };
  }

  private queueWorldTileAction(screenX: number, screenY: number, pressAction: boolean): boolean {
    const view = this.latestView;
    if (view === null) return false;
    const layout = computeScreenLayout(this.container.clientWidth, this.container.clientHeight);
    const worldX = view.camera.x + (screenX - (layout.x + layout.w * 0.5)) / layout.scale;
    const worldY = view.camera.y + (screenY - (layout.y + layout.h * 0.5)) / layout.scale;
    const tileX = Math.floor(worldX / TILE_SIZE);
    const tileY = Math.floor(worldY / TILE_SIZE);
    const distance = Math.abs(tileX - view.player.tileX) + Math.abs(tileY - view.player.tileY);
    const toolRange = view.selectedTool.id === 'hands' ? 1 : 8;
    if (distance > toolRange) return false;
    if (pressAction) this.queuedMask |= INPUT_ACTION;
    this.queuedTargetTileX = tileX;
    this.queuedTargetTileY = tileY;
    this.heldTargetTileX = tileX;
    this.heldTargetTileY = tileY;
    this.heldAction = true;
    return true;
  }

  private sampleGamepad(): { heldMask: number; queuedMask: number; actionHeld: boolean } {
    const pads = navigator.getGamepads?.() ?? [];
    const pressedNow = new Set<string>();
    let heldMask = 0;
    let queuedMask = 0;
    let actionHeld = false;

    for (const pad of pads) {
      if (pad === null) continue;
      const modalScope = this.activeFocusScope();
      if (modalScope === null) {
        const x = pad.axes[0] ?? 0;
        const y = pad.axes[1] ?? 0;
        if (x < -0.35 || pad.buttons[14]?.pressed) heldMask |= INPUT_LEFT;
        if (x > 0.35 || pad.buttons[15]?.pressed) heldMask |= INPUT_RIGHT;
        if (y < -0.35 || pad.buttons[12]?.pressed) heldMask |= INPUT_UP;
        if (y > 0.35 || pad.buttons[13]?.pressed) heldMask |= INPUT_DOWN;
      }

      for (const button of [0, 1, 2, 3, 4, 5, 6, 7, 12, 13, 14, 15]) {
        if (!pad.buttons[button]?.pressed) continue;
        const key = `${pad.index}:${button}`;
        pressedNow.add(key);
        if (this.previousGamepadButtons.has(key)) continue;
        if (modalScope === 'inventory') this.queueInventoryGamepad(button);
        else if (modalScope === 'buildMenu') this.queueBuildGamepad(button);
        else if (modalScope === 'buildingPanel') this.queuePanelGamepad(button);
        else if (modalScope === 'techTree') this.queueTechGamepad(button);
        else if (modalScope === 'toolsPanel') this.queueToolsGamepad(button);
        else if (modalScope === 'debugPanel') this.queueDebugGamepad(button);
        else queuedMask |= this.queueWorldGamepad(button);
      }

      if (pad.buttons[0]?.pressed && modalScope === null) actionHeld = true;
    }

    this.previousGamepadButtons.clear();
    for (const key of pressedNow) this.previousGamepadButtons.add(key);
    return { heldMask, queuedMask, actionHeld };
  }

  private queueInventoryGamepad(button: number): void {
    if (button === 1) this.queuedMask |= INPUT_CLOSE_PANEL;
    if (button === 12) this.queueRelativeFocus('inventory', -5);
    if (button === 13) this.queueRelativeFocus('inventory', 5);
    if (button === 14) this.queueRelativeFocus('inventory', -1);
    if (button === 15) this.queueRelativeFocus('inventory', 1);
  }

  private queueBuildGamepad(button: number): void {
    if (button === 0) this.queuedBuildCommand = this.buildCommandForCurrentFocus();
    if (button === 1) this.queuedBuildCommand = 'cancel';
    if (button === 12 || button === 13 || button === 14 || button === 15) this.queueRelativeFocus('buildMenu', 1);
  }

  private queuePanelGamepad(button: number): void {
    if (button === 0) this.queuedPanelCommand = this.panelCommandForCurrentFocus();
    if (button === 1) this.queuedPanelCommand = 'close';
    if (button === 12 || button === 14) this.queueRelativeFocus('buildingPanel', -1);
    if (button === 13 || button === 15) this.queueRelativeFocus('buildingPanel', 1);
  }

  private queueTechGamepad(button: number): void {
    if (button === 0) this.queuedTechCommand = this.techCommandForCurrentFocus();
    if (button === 1) this.queuedMask |= INPUT_CLOSE_PANEL;
    if (button === 12 || button === 13 || button === 14 || button === 15) this.queueRelativeFocus('techTree', 1);
  }

  private queueToolsGamepad(button: number): void {
    if (button === 0) this.queuedToolCommand = this.toolCommandForCurrentFocus();
    if (button === 1) this.queuedMask |= INPUT_CLOSE_PANEL;
    if (button === 12 || button === 14) this.queueRelativeFocus('toolsPanel', -1);
    if (button === 13 || button === 15) this.queueRelativeFocus('toolsPanel', 1);
  }

  private queueDebugGamepad(button: number): void {
    if (button === 0) this.queuedDebugCommand = this.debugCommandForCurrentFocus();
    if (button === 1) this.queuedMask |= INPUT_CLOSE_PANEL;
    if (button === 12 || button === 14) this.queueRelativeFocus('debugPanel', -1);
    if (button === 13 || button === 15) this.queueRelativeFocus('debugPanel', 1);
  }

  private queueWorldGamepad(button: number): number {
    if (button === 0) return INPUT_ACTION;
    if (button === 1) return INPUT_OPEN_DEBUG_PANEL;
    if (button === 3) return INPUT_OPEN_BUILD_MENU;
    if (button === 2) return INPUT_OPEN_TECH_TREE;
    if (button === 6) return INPUT_OPEN_INVENTORY;
    return 0;
  }

  private queueFocusNavigation(event: KeyboardEvent): void {
    const direction = menuNavigateForKey(event);
    if (direction === null) return;
    const scope = this.activeFocusScope();
    if (scope === null) return;
    if (scope === 'inventory') {
      const delta = direction === 'left' ? -1 : direction === 'right' ? 1 : direction === 'up' ? -5 : 5;
      this.queueRelativeFocus(scope, delta);
      return;
    }
    const delta = direction === 'up' || direction === 'left' ? -1 : 1;
    this.queueRelativeFocus(scope, delta);
  }

  private queueRelativeFocus(scope: UiFocusScope, delta: number): void {
    this.queueAbsoluteFocus(scope, this.currentFocusIndex(scope) + delta);
  }

  private queueAbsoluteFocus(scope: UiFocusScope, index: number): void {
    this.queuedMask &= ~(INPUT_UP | INPUT_RIGHT | INPUT_DOWN | INPUT_LEFT);
    this.queuedTargetTileX = undefined;
    this.queuedTargetTileY = undefined;
    this.queuedInventoryMove = undefined;
    this.queuedTransferItem = undefined;
    this.queuedPanelItemMove = undefined;
    this.queuedPanelCommand = undefined;
    this.queuedBuildCommand = undefined;
    this.queuedTechCommand = undefined;
    this.queuedItemCommand = undefined;
    this.queuedToolCommand = undefined;
    this.queuedDebugCommand = undefined;
    const wrapped = this.wrapFocusIndex(scope, index);
    this.queuedUiFocus = { scope, index: wrapped };
    if (scope === 'inventory') this.queuedSelectedSlot = wrapped;
  }

  private clearQueuedOneShots(): void {
    this.queuedMask = 0;
    this.queuedTargetTileX = undefined;
    this.queuedTargetTileY = undefined;
    this.queuedSelectedSlot = undefined;
    this.queuedInventoryMove = undefined;
    this.queuedTransferItem = undefined;
    this.queuedPanelItemMove = undefined;
    this.queuedUiFocus = undefined;
    this.queuedPanelCommand = undefined;
    this.queuedBuildCommand = undefined;
    this.queuedTechCommand = undefined;
    this.queuedItemCommand = undefined;
    this.queuedToolCommand = undefined;
    this.queuedDebugCommand = undefined;
  }

  private activeFocusScope(): UiFocusScope | null {
    const view = this.latestView;
    if (view?.inventoryPanel.open === true) return 'inventory';
    if (view?.buildMenu.open === true) return 'buildMenu';
    if (view?.techTree.open === true) return 'techTree';
    if (view?.toolsPanel.open === true) return 'toolsPanel';
    if (view?.debugPanel.open === true) return 'debugPanel';
    if (view?.buildingPanel !== null && view?.buildingPanel !== undefined) return 'buildingPanel';
    return null;
  }

  private currentFocusIndex(scope: UiFocusScope): number {
    if (this.queuedUiFocus?.scope === scope) return this.queuedUiFocus.index;
    const view = this.latestView;
    if (view === null) return 0;
    if (scope === 'inventory') return view.inventoryPanel.focusedSlot;
    if (scope === 'buildMenu') return view.buildMenu.focusedIndex;
    if (scope === 'techTree') return view.techTree.focusedIndex;
    if (scope === 'toolsPanel') return view.toolsPanel.focusedIndex;
    if (scope === 'debugPanel') return view.debugPanel.focusedIndex;
    return view.buildingPanel === null ? 0 : panelCommandIndex(view.buildingPanel.focusedCommand);
  }

  private wrapFocusIndex(scope: UiFocusScope, index: number): number {
    const count = scope === 'inventory'
      ? (this.latestView?.inventoryPanel.slots.length ?? 1)
      : scope === 'buildMenu'
        ? (this.latestView?.buildMenu.options.length ?? 2)
        : scope === 'techTree'
          ? (this.latestView?.techTree.options.length ?? 2)
          : scope === 'toolsPanel'
            ? (this.latestView?.toolsPanel.recipes.length ?? 2)
            : scope === 'debugPanel'
              ? (this.latestView?.debugPanel.options.length ?? 2)
              : 5;
    if (count <= 0) return 0;
    return ((index % count) + count) % count;
  }

  private buildCommandForCurrentFocus(): BuildCommand {
    const view = this.latestView;
    if (view === null) return 'recycler';
    const option = view.buildMenu.options[this.currentFocusIndex('buildMenu')] ?? view.buildMenu.options[0];
    return option?.type ?? 'recycler';
  }

  private techCommandForCurrentFocus(): TechId {
    const view = this.latestView;
    if (view === null) return 'gather_speed';
    const option = view.techTree.options[this.currentFocusIndex('techTree')] ?? view.techTree.options[0];
    return option?.id ?? 'gather_speed';
  }

  private toolCommandForCurrentFocus(): NonNullable<Input['toolCommand']> {
    const view = this.latestView;
    if (view === null) return 'grenade';
    const recipe = view.toolsPanel.recipes[this.currentFocusIndex('toolsPanel')] ?? view.toolsPanel.recipes[0];
    return recipe?.id ?? 'grenade';
  }

  private debugCommandForCurrentFocus(): NonNullable<Input['debugCommand']> {
    const view = this.latestView;
    if (view === null) return 'grantTechPoint';
    const option = view.debugPanel.options[this.currentFocusIndex('debugPanel')] ?? view.debugPanel.options[0];
    return option?.command ?? 'grantTechPoint';
  }

  private panelCommandForCurrentFocus(): NonNullable<Input['panelCommand']> {
    return (['depositSelected', 'depositAll', 'collectOutput', 'removeBuilding', 'close'] as const)[this.wrapFocusIndex('buildingPanel', this.currentFocusIndex('buildingPanel'))];
  }

  private updateTooltip(x: number, y: number, layout: ScreenLayout): void {
    const text = this.tooltipTextAt(x, y, layout);
    if (text === null) {
      this.tooltip.hidden = true;
      return;
    }
    this.tooltip.textContent = text;
    this.tooltip.hidden = false;
    const left = Math.min(this.container.clientWidth - 220, x + 14);
    const top = Math.max(12, y - 34);
    this.tooltip.style.transform = `translate(${Math.max(10, left)}px, ${top}px)`;
  }

  private tooltipTextAt(x: number, y: number, layout: ScreenLayout): string | null {
    const view = this.latestView;
    if (view?.inventoryPanel.open === true) {
      const panel = computeInventoryPanelRects(layout, view.inventoryPanel.slots.length);
      const slot = panel.slots.findIndex((rect) => contains(rect, x, y));
      if (slot >= 0) return itemTooltip(view.inventoryPanel.slots[slot]?.item ?? null, 'Empty inventory slot. Drag items here.');
      if (contains(panel.close, x, y)) return 'Close inventory.';
      return null;
    }
    if (view?.buildMenu.open === true) {
      const menu = computeBuildMenuRects(layout);
      const optionIndex = menu.options.findIndex((rect) => contains(rect, x, y));
      if (optionIndex >= 0) return `Select ${view.buildMenu.options[optionIndex]?.name ?? 'building'} placement if you can pay the cost.`;
      if (contains(menu.close, x, y)) return 'Cancel building placement.';
      return null;
    }
    if (view?.techTree.open === true) {
      const menu = computeTechTreeRects(layout);
      const optionIndex = menu.options.findIndex((rect) => contains(rect, x, y));
      if (optionIndex >= 0) return view.techTree.options[optionIndex]?.description ?? 'Spend a tech point.';
      if (contains(menu.close, x, y)) return 'Close tech tree.';
      return null;
    }
    if (view?.toolsPanel.open === true) {
      const menu = computeToolsPanelRects(layout);
      const optionIndex = menu.options.findIndex((rect) => contains(rect, x, y));
      if (optionIndex >= 0) {
        const recipe = view.toolsPanel.recipes[optionIndex];
        return recipe === undefined ? 'Craft a tool.' : `${recipe.name}: ${recipe.description} ${recipe.status}.`;
      }
      if (contains(menu.close, x, y)) return 'Close tools panel.';
      return null;
    }
    if (view?.debugPanel.open === true) {
      const menu = computeDebugPanelRects(layout);
      const optionIndex = menu.options.findIndex((rect) => contains(rect, x, y));
      if (optionIndex >= 0) {
        const option = view.debugPanel.options[optionIndex];
        return option === undefined ? 'Run a debug grant.' : `${option.name}: ${option.description}`;
      }
      if (contains(menu.close, x, y)) return 'Close debug panel.';
      return null;
    }
    if (view?.buildingPanel !== null && view?.buildingPanel !== undefined) {
      const panel = computeBuildingPanelRects(layout, view.buildingPanel.playerInventory.length, view.buildingPanel.inbox.length, view.buildingPanel.outbox.length, view.buildingPanel.storage.length);
      const playerSlot = panel.playerSlots.findIndex((rect) => contains(rect, x, y));
      if (playerSlot >= 0) return itemTooltip(view.buildingPanel.playerInventory[playerSlot]?.item ?? null, 'Empty player inventory slot. Drag items here.');
      const inputSlot = panel.inboxSlots.findIndex((rect) => contains(rect, x, y));
      if (inputSlot >= 0) return itemTooltip(view.buildingPanel.inbox[inputSlot] ?? null, 'Building input slot.');
      const outputSlot = panel.outboxSlots.findIndex((rect) => contains(rect, x, y));
      if (outputSlot >= 0) return itemTooltip(view.buildingPanel.outbox[outputSlot] ?? null, 'Building output slot.');
      const storageSlot = panel.storageSlots.findIndex((rect) => contains(rect, x, y));
      if (storageSlot >= 0) return itemTooltip(view.buildingPanel.storage[storageSlot] ?? null, 'Storage slot.');
      if (contains(panel.deposit, x, y)) return 'Deposit the focused inventory item.';
      if (contains(panel.depositAll, x, y)) return 'Deposit every valid item from your inventory.';
      if (contains(panel.collect, x, y)) return view.buildingPanel.type === 'storage' ? 'Use shift-click to take specific storage items.' : 'Collect all output into your inventory.';
      if (contains(panel.remove, x, y)) return 'Remove this empty building. No refund.';
      if (contains(panel.close, x, y)) return 'Close this building panel.';
      return null;
    }
    if (contains(computeInventoryButtonRect(layout), x, y)) return 'I: open inventory.';
    if (contains(computeActionButtonRect(layout), x, y)) return view?.actionTarget.label ?? 'Use the highlighted action.';
    const buildRects = computeBuildButtonRects(layout);
    if (contains(buildRects.build, x, y)) return 'V: open the building menu.';
    if (contains(buildRects.tech, x, y)) return 'T: open the tech tree.';
    return null;
  }

  private buildingPanelSlotAt(
    x: number,
    y: number,
    panel: ReturnType<typeof computeBuildingPanelRects>,
  ): { location: NonNullable<Input['panelItemMove']>['from']; slot: number } | null {
    const playerSlot = panel.playerSlots.findIndex((rect) => contains(rect, x, y));
    if (playerSlot >= 0) return { location: 'player', slot: playerSlot };
    const inputSlot = panel.inboxSlots.findIndex((rect) => contains(rect, x, y));
    if (inputSlot >= 0) return { location: 'buildingInput', slot: inputSlot };
    const outputSlot = panel.outboxSlots.findIndex((rect) => contains(rect, x, y));
    if (outputSlot >= 0) return { location: 'buildingOutput', slot: outputSlot };
    const storageSlot = panel.storageSlots.findIndex((rect) => contains(rect, x, y));
    if (storageSlot >= 0) return { location: 'buildingStorage', slot: storageSlot };
    return null;
  }
}

function itemTooltip(item: NonNullable<ViewModel['inventory']['slots'][number]['item']> | null, emptyText: string): string {
  if (item === null) return emptyText;
  const quantity = item.quantity > 1 ? ` x${item.quantity}` : '';
  if (item.kind === 'trash') return `Trash${quantity}. Stacks up to 10. Drag to move this stack.`;
  if (item.kind === 'item') return `${item.itemId === 'grenade' ? 'Grenade' : 'Flamethrower'}${quantity}. Drag to move.`;
  return `${resourceName(item.resource)}${quantity}. Drag to move or stack.`;
}

function resourceName(resource: string | undefined): string {
  if (resource === 'plastic') return 'Plastic';
  if (resource === 'glass') return 'Glass';
  if (resource === 'biomass') return 'Biomass';
  return 'Scrap';
}
